<!--
  Termine und Terminwünsche.

  Ein Wunsch ist ausdrücklich ein Wunsch, kein gebuchter Termin — das steht
  auch so auf der Seite. Alles andere führt zu Missverständnissen an der
  Haustür.

  Die Zeitwahl ist seit Etappe 19 ein Slot-Picker statt Freitext: Es werden
  nur Zeiten angeboten, die laut Buddy-Kalender tatsächlich frei sind. Welche
  Zeiten das konkret sind (Buddy-Arbeitszeiten, wer wann Urlaub hat), bleibt
  bewusst intern — der Kunde sieht nur „frei" oder „belegt", keine Muster.
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import HilfeLink from '../lib/HilfeLink.svelte';
  import { router } from '../lib/router.svelte.ts';
  import { zeitpunkt, datum } from '../lib/sitzung.svelte.ts';

  interface Termin {
    id: string;
    beginn: string;
    ende: string | null;
    art: string;
    status: string;
    ort: string | null;
    titel: string | null;
    buddy_name: string | null;
    ticket_nummer: string | null;
  }

  interface Wunsch {
    id: string;
    wunsch_von: string;
    art: string;
    anliegen: string;
    status: string;
    antwort: string | null;
    gestellt_am: string;
    ticket_id: string | null;
    ticket_nummer: string | null;
    ticket_titel: string | null;
  }

  /** Offene Anfrage, an die sich ein Terminwunsch hängen kann. */
  interface Anfrage {
    id: string;
    nummer: string;
    titel: string;
    status: string;
    geschlossen_am: string | null;
  }

  interface Slot {
    beginn: string;
    ende: string;
  }

  interface Tag {
    datum: string;
    slots: Slot[];
  }

  const ART: Record<string, string> = {
    remote: 'per Fernwartung',
    vor_ort: 'bei Ihnen vor Ort',
    telefon: 'telefonisch',
  };

  const STATUS: Record<string, string> = {
    geplant: 'geplant',
    bestaetigt: 'fest vereinbart',
    wahrgenommen: 'stattgefunden',
    abgesagt: 'abgesagt',
  };

  // Die Zeitleiste spannt sichtbar von 08:00 bis 21:00 — eine Viertelstunde
  // ist eine Rasterzeile, 45 Minuten sind drei Zeilen.
  const RASTER_START_STUNDE = 8;
  const RASTER_END_STUNDE = 21;
  const RASTER_ZEILEN = (RASTER_END_STUNDE - RASTER_START_STUNDE) * 4;

  let termine = $state<Termin[]>([]);
  let wuensche = $state<Wunsch[]>([]);
  let tage = $state<Tag[]>([]);
  let gewaehlterTag = $state<string | null>(null);
  let gewaehlterSlot = $state<Slot | null>(null);
  let fehler = $state<string | null>(null);
  let hinweis = $state<string | null>(null);
  let sendet = $state(false);
  let angaben = $state({ art: 'remote', anliegen: '' });

  /*
   * Wozu gehört der Termin? Seit 04.09.2026 wählt der Kunde das selbst: eine
   * seiner offenen Anfragen oder „ein neues Anliegen". Vorher legte jeder
   * Wunsch stillschweigend ein neues Ticket an — wer erst schrieb und dann
   * einen Termin wollte, hatte zwei Vorgänge (Leon Müller, T-1041/T-1042).
   *
   * '' = neues Anliegen. Vorbelegt über `#/termine/anfrage/<id>` (Knopf
   * „Termin dazu vorschlagen" in der Anfrage).
   */
  let anfragen = $state<Anfrage[]>([]);
  let zuordnung = $state('');
  const vorbelegteAnfrage = $derived(
    router.segmente[0] === 'termine' && router.segmente[1] === 'anfrage' ? (router.segmente[2] ?? '') : '',
  );
  const gewaehlteAnfrage = $derived(anfragen.find((a) => a.id === zuordnung) ?? null);

  async function laden(): Promise<void> {
    try {
      const [t, f, a] = await Promise.all([
        api.get<{ termine: Termin[]; wuensche: Wunsch[] }>('/api/portal/termine'),
        api.get<{ tage: Tag[] }>('/api/portal/freie-termine'),
        api.get<{ tickets: Anfrage[] }>('/api/portal/tickets'),
      ]);
      termine = t.termine;
      wuensche = t.wuensche;
      tage = f.tage;
      anfragen = a.tickets.filter(
        (x) => !x.geschlossen_am && x.status !== 'erledigt' && x.status !== 'archiviert',
      );
      // Vorbelegung nur übernehmen, wenn die Anfrage wirklich noch offen ist —
      // sonst stünde im Feld etwas, das der Server ablehnt.
      if (vorbelegteAnfrage && anfragen.some((x) => x.id === vorbelegteAnfrage)) {
        zuordnung = vorbelegteAnfrage;
      } else if (!anfragen.some((x) => x.id === zuordnung)) {
        zuordnung = '';
      }
      if (!gewaehlterTag || !tage.some((tg) => tg.datum === gewaehlterTag)) {
        gewaehlterTag = tage.find((tg) => tg.slots.length > 0)?.datum ?? tage[0]?.datum ?? null;
      }
      gewaehlterSlot = null;
      fehler = null;
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das konnten wir gerade nicht laden';
    }
  }

  $effect(() => {
    void laden();
  });

  const kommende = $derived(termine.filter((t) => new Date(t.beginn) >= new Date()));
  const vergangene = $derived(termine.filter((t) => new Date(t.beginn) < new Date()));
  const tagSlots = $derived(tage.find((tg) => tg.datum === gewaehlterTag)?.slots ?? []);
  const stundenmarken = $derived(
    Array.from({ length: RASTER_END_STUNDE - RASTER_START_STUNDE + 1 }, (_, i) => RASTER_START_STUNDE + i),
  );

  function tagLabel(iso: string): { wochentag: string; tag: string } {
    const [jahr, monat, t] = iso.split('-').map(Number);
    const d = new Date(jahr!, (monat ?? 1) - 1, t);
    return {
      wochentag: d.toLocaleDateString('de-DE', { weekday: 'short' }),
      tag: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
    };
  }

  function slotZeit(iso: string): string {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  /** 1-indexierte Rasterzeile für den Beginn eines Slots. */
  function slotZeile(iso: string): number {
    const d = new Date(iso);
    const minutenAbStart = (d.getHours() - RASTER_START_STUNDE) * 60 + d.getMinutes();
    return Math.max(1, Math.round(minutenAbStart / 15) + 1);
  }

  function slotWaehlen(slot: Slot): void {
    gewaehlterSlot = gewaehlterSlot?.beginn === slot.beginn ? null : slot;
  }

  async function absenden(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    if (!gewaehlterSlot) {
      fehler = 'Bitte zuerst eine freie Zeit auswählen';
      return;
    }
    sendet = true;
    fehler = null;
    try {
      const zuAnfrage = gewaehlteAnfrage;
      await api.post('/api/portal/terminwunsch', {
        wunsch_von: gewaehlterSlot.beginn,
        wunsch_bis: gewaehlterSlot.ende,
        art: angaben.art,
        anliegen: angaben.anliegen.trim(),
        ticket_id: zuAnfrage?.id ?? undefined,
      });
      hinweis =
        (zuAnfrage
          ? `Ihr Terminwunsch hängt jetzt an Ihrer Anfrage „${zuAnfrage.titel}" (${zuAnfrage.nummer}). `
          : 'Ihr Wunsch ist bei uns. ') +
        'Fest vereinbart ist der Termin erst, wenn wir ihn bestätigt haben — Sie bekommen eine E-Mail, ' +
        'bitte schauen Sie auch in den Spam-Ordner.';
      angaben = { art: 'remote', anliegen: '' };
      zuordnung = '';
      gewaehlterSlot = null;
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das hat gerade nicht geklappt';
      // Der gewählte Slot könnte inzwischen vergeben sein — Liste neu laden,
      // damit er nicht mehr als frei angezeigt wird.
      await laden();
    } finally {
      sendet = false;
    }
  }
</script>

<h1>Termine</h1>
<HilfeLink slug="kundenbereich-anfrage-termin" text="Anleitung: Anfrage stellen und Termin wünschen" />

{#if hinweis}<p class="erfolg">{hinweis}</p>{/if}
{#if fehler}<p class="fehler">{fehler}</p>{/if}

<div class="karte">
  <h2>Vereinbarte Termine</h2>
  {#if kommende.length === 0}
    <p class="leise">Zurzeit ist kein Termin vereinbart.</p>
  {:else}
    <ul class="eintraege">
      {#each kommende as termin (termin.id)}
        <li>
          <div class="zeile">
            <strong>{zeitpunkt(termin.beginn)}</strong>
            <span class="marke" class:gut={termin.status === 'bestaetigt'}>
              {STATUS[termin.status] ?? termin.status}
            </span>
          </div>
          <div class="leise klein">
            {ART[termin.art] ?? termin.art}
            {#if termin.buddy_name} · mit {termin.buddy_name}{/if}
            {#if termin.ort} · {termin.ort}{/if}
            {#if termin.ticket_nummer} · zu {termin.ticket_nummer}{/if}
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<div class="karte">
  <h2>Einen Termin vorschlagen</h2>
  <p class="leise">
    Wählen Sie eine freie Zeit — frühestens in vier Stunden, damit wir uns darauf einstellen
    können. Wir bestätigen den Termin, erst dann steht er fest.
  </p>
  {#if gewaehlteAnfrage}
    <p class="wichtig klein">
      Für Ihre Anfrage „{gewaehlteAnfrage.titel}" ({gewaehlteAnfrage.nummer}).
    </p>
  {/if}

  <div class="tagesstreifen" role="group" aria-label="Tag wählen">
    {#each tage as tag (tag.datum)}
      {@const label = tagLabel(tag.datum)}
      <button
        type="button"
        class="tag-chip"
        aria-pressed={gewaehlterTag === tag.datum}
        disabled={tag.slots.length === 0}
        onclick={() => {
          gewaehlterTag = tag.datum;
          gewaehlterSlot = null;
        }}
      >
        <span class="wochentag">{label.wochentag}</span>
        {label.tag}
      </button>
    {/each}
  </div>

  {#if tagSlots.length === 0}
    <p class="leise zeitleiste-leer">An diesem Tag ist gerade nichts frei — bitte einen anderen Tag wählen.</p>
  {:else}
    <div
      class="zeitleiste"
      style={`grid-template-rows: repeat(${RASTER_ZEILEN}, 12px);`}
      role="group"
      aria-label="Uhrzeit wählen"
    >
      {#each stundenmarken as stunde, i (stunde)}
        <div class="stundentext" style={`grid-row: ${i * 4 + 1} / span 1;`}>
          {String(stunde).padStart(2, '0')}
        </div>
        <div class="stundenlinie" style={`grid-row: ${i * 4 + 1} / span 1;`}></div>
      {/each}
      {#each tagSlots as slot (slot.beginn)}
        <button
          type="button"
          class="slot-chip"
          style={`grid-row: ${slotZeile(slot.beginn)} / span 3;`}
          aria-pressed={gewaehlterSlot?.beginn === slot.beginn}
          onclick={() => slotWaehlen(slot)}
        >
          {slotZeit(slot.beginn)}
        </button>
      {/each}
    </div>
  {/if}

  <form onsubmit={absenden}>
    {#if gewaehlterSlot}
      <p class="wichtig klein">
        Gewählt: {tagLabel(gewaehlterTag ?? '').tag}, {slotZeit(gewaehlterSlot.beginn)}–{slotZeit(
          gewaehlterSlot.ende,
        )} Uhr
      </p>
    {/if}

    {#if anfragen.length > 0}
      <label for="zuordnung">Wozu gehört der Termin?</label>
      <select id="zuordnung" bind:value={zuordnung}>
        {#each anfragen as a (a.id)}
          <option value={a.id}>Zu meiner Anfrage „{a.titel}" ({a.nummer})</option>
        {/each}
        <option value="">Ein neues Anliegen</option>
      </select>
    {/if}

    <label for="art">Wie soll es stattfinden?</label>
    <select id="art" bind:value={angaben.art}>
      <option value="remote">per Fernwartung (wir schauen auf Ihren Bildschirm)</option>
      <option value="vor_ort">bei Ihnen vor Ort</option>
      <option value="telefon">telefonisch</option>
    </select>

    {#if gewaehlteAnfrage}
      <label for="anliegen">Noch etwas dazu? <span class="leise">(nicht nötig)</span></label>
      <textarea id="anliegen" bind:value={angaben.anliegen}></textarea>
    {:else}
      <label for="anliegen">Worum soll es gehen?</label>
      <textarea id="anliegen" bind:value={angaben.anliegen} required></textarea>
    {/if}

    <div class="knopfreihe">
      <button
        type="submit"
        disabled={sendet || !gewaehlterSlot || (!gewaehlteAnfrage && !angaben.anliegen.trim())}
      >
        {sendet ? 'Wird gesendet …' : 'Terminwunsch senden'}
      </button>
    </div>
  </form>
</div>

{#if wuensche.length > 0}
  <div class="karte">
    <h2>Ihre Terminwünsche</h2>
    <ul class="eintraege">
      {#each wuensche as w (w.id)}
        <li>
          <div class="zeile">
            <strong>{zeitpunkt(w.wunsch_von)}</strong>
            <span
              class="marke"
              class:gut={w.status === 'angenommen'}
              class:schlecht={w.status === 'abgelehnt'}
              class:achtung={w.status === 'offen'}
            >
              {w.status === 'offen' ? 'wir melden uns' : w.status === 'angenommen' ? 'bestätigt' : 'nicht möglich'}
            </span>
          </div>
          <div class="leise klein">
            {w.anliegen} · gestellt am {datum(w.gestellt_am)}
            {#if w.ticket_nummer && w.ticket_id}
              · zu <a href="#/anfragen/{w.ticket_id}">{w.ticket_nummer}</a>
            {/if}
          </div>
          {#if w.antwort}<p class="klein">{w.antwort}</p>{/if}
        </li>
      {/each}
    </ul>
  </div>
{/if}

{#if vergangene.length > 0}
  <div class="karte">
    <h2>Frühere Termine</h2>
    <ul class="eintraege">
      {#each vergangene.slice(0, 10) as termin (termin.id)}
        <li>
          <div class="zeile">
            <span>{zeitpunkt(termin.beginn)}</span>
            <span class="marke">{STATUS[termin.status] ?? termin.status}</span>
          </div>
        </li>
      {/each}
    </ul>
  </div>
{/if}
